import { OrganizationTheme } from '../../models/theme.interface';

/**
 * Ovivio Base Theme Configuration
 *
 * This is the base theme configuration that contains all shared Ovivio design system
 * properties. Both Ovivio and DoenKids themes extend this base configuration.
 *
 * Key features:
 * - Typography uses Moranga for headings and Figtree for body text
 * - Warm, accessible color palette with neutrals
 * - Modern UI patterns with rounded corners and subtle shadows
 */

export const ovivioBaseColors = {
  // Semantic colors
  success: '#2E7D32',
  error: '#C62828',
  info: '#0D8CED',

  // Neutral colors
  white: '#FFFFFF',
  grey: {
    100: '#F9F7F5',      // Warm gray background
    300: '#E0E0E0',      // Deactivated state
    400: '#999999',      // Subdued text on dark
    500: '#5C5463',      // Subdued text on light
    700: '#424242',      // Dark grey
    900: '#1A0F24',      // Main text
  },
  black: '#000',
  lightgrey: '#E0E0E0',
  whitesmoke: '#F9F7F5',

  // Background colors
  cardBackground: '#F9F7F5',
  pageBackground: '#F9F7F5',
  sidebarBackground: '#EFECE7',
  sidebarOverlay: 'linear-gradient(58deg, #EFECE7, #EFECE7)',
  rimBackground: '#EFECE7',
  organizationUnitSelectorBackground: '#F9F7F5',

  // Text colors
  text: {
    main: '#1A0F24',
    subdued: '#5C5463',
    onPrimary: '#FFFFFF',
    onSecondary: '#FFFFFF',
    onWarn: '#FFFFFF',
    filterHeading: '#424242',
    sidebarIcon: '#5C5463',
    sidebarText: '#1A0F24',
  },

  // Transparent overlays
  transparent: {
    0: '#00000000',
    50: '#0000000d',
    120: '#0000001f',
    140: '#00000024',
    200: '#00000033',
    300: '#0000004d',
    400: '#00000066',
    500: 'rgba(0, 0, 0, 0.5)',
    600: '#00000099',
    700: 'rgba(0, 0, 0, 0.7)',
    900: '#000000e6',
  },

  // Context-specific colors
  attachmentLink: '#4D85FF',

  // Box shadows
  videoBoxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',

  // Status colors (for publication workflow)
  status: {
    1: { bg: '#ffcc00' },
    2: { bg: '#FA5329' },
    3: { bg: '#83bb26' },
    4: { bg: '#E0E0E0', color: '#a5a5a5' },
    5: { bg: '#ECEDDC', color: '#ECEDDC' },
  },

  // Message colors
  message: {
    subtitle: '#FA5329',
    paragraph: '#5C5463',
  },
};

export const ovivioBaseFonts = {
  // Global fonts
  primary: '"Figtree", "Roboto", sans-serif',
  secondary: '"Moranga", "Figtree", "Roboto", sans-serif',
  logo: '"Moranga", sans-serif',

  // Area-specific fonts
  sidebar: '"Figtree", "Roboto", sans-serif',
  content: '"Figtree", "Roboto", sans-serif',

  // Font sizes
  sizes: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    md: '18px',
    lg: '20px',
    xl: '24px',
  },

  // Font weights
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

export const ovivioBaseUI = {
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },

  spacing: {
    unit: '8px',
  },

  rimWidth: '24px',
  avatarBorder: 'none',

  logoMaxHeight: '48px',
  logoJustify: 'flex-start',

  shadows: {
    light: '0 2px 8px rgba(0, 0, 0, 0.08)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.12)',
    heavy: '0 8px 32px rgba(0, 0, 0, 0.16)',
  },
};

/**
 * Helper function to merge base theme with brand-specific overrides
 */
export function createThemeFromBase(overrides: {
  id: string;
  name: string;
  brandName?: string;
  domains: string[];
  colors?: any;
  fonts?: any;
  ui?: any;
  assets: any;
  icons?: any;
  auth0?: any;
}): OrganizationTheme {
  const merged: any = {
    id: overrides.id,
    name: overrides.name,
    brandName: overrides.brandName,
    domains: overrides.domains,
    colors: {
      ...ovivioBaseColors,
      ...overrides.colors,
      primary: overrides.colors?.primary,
      secondary: overrides.colors?.secondary,
      warn: overrides.colors?.warn,
      text: {
        ...ovivioBaseColors.text,
        ...(overrides.colors?.text || {}),
      },
      grey: {
        ...ovivioBaseColors.grey,
        ...(overrides.colors?.grey || {}),
      },
      transparent: {
        ...ovivioBaseColors.transparent,
        ...(overrides.colors?.transparent || {}),
      },
      status: {
        ...ovivioBaseColors.status,
        ...(overrides.colors?.status || {}),
      },
      message: {
        ...ovivioBaseColors.message,
        ...(overrides.colors?.message || {}),
      },
    },
    fonts: {
      ...ovivioBaseFonts,
      ...overrides.fonts,
      sizes: {
        ...ovivioBaseFonts.sizes,
        ...(overrides.fonts?.sizes || {}),
      },
      weights: {
        ...ovivioBaseFonts.weights,
        ...(overrides.fonts?.weights || {}),
      },
    },
    ui: {
      ...ovivioBaseUI,
      ...overrides.ui,
      borderRadius: {
        ...ovivioBaseUI.borderRadius,
        ...(overrides.ui?.borderRadius || {}),
      },
      spacing: {
        ...ovivioBaseUI.spacing,
        ...(overrides.ui?.spacing || {}),
      },
      shadows: {
        ...ovivioBaseUI.shadows,
        ...(overrides.ui?.shadows || {}),
      },
    },
    assets: overrides.assets,
    icons: overrides.icons,
    auth0: overrides.auth0,
  };

  return merged as OrganizationTheme;
}
